import React from 'react';
import { Card } from './ui/card';
import { Cog, Microscope, Scale, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

export const Industries = () => {
  const industries = [
    {
      name: "Engineering",
      icon: Cog,
      description: "Engineering firms need business minds for project management, operations, and strategy. Technical innovation requires business excellence.",
      opportunities: ["Project Management", "Operations Strategy", "Technical Consulting", "Business Development"],
      gradient: "from-orange-50 to-red-50",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    {
      name: "Science & Healthcare",
      icon: Microscope,
      description: "Pharmaceutical, biotech, and research organizations rely on business expertise for operations, finance, and regulatory compliance.",
      opportunities: ["Pharmaceutical Operations", "Biotech Finance", "Healthcare Consulting", "Regulatory Affairs"],
      gradient: "from-cyan-50 to-blue-50",
      iconBg: "bg-cyan-100",
      iconColor: "text-cyan-600"
    },
    {
      name: "Law & Policy",
      icon: Scale,
      description: "Corporate law, government agencies, and policy organizations value business acumen for compliance, operations, and strategic planning.",
      opportunities: ["Corporate Compliance", "Policy Analysis", "Legal Operations", "Risk Management"],
      gradient: "from-slate-100 to-gray-50",
      iconBg: "bg-slate-200",
      iconColor: "text-slate-700"
    }
  ];

  return (
    <section id="industries" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Industries We Connect
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Telfer Gateway Society focuses on three key sectors where business skills create the most impact—
            and where Telfer students are rarely exposed.
          </p>
        </div>

        {/* Industry Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {industries.map((industry, index) => {
            const Icon = industry.icon;
            return (
              <Card
                key={index}
                className={`p-8 bg-gradient-to-br ${industry.gradient} border-2 border-slate-200 hover:border-[#8B1538]/30 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}
              >
                <div className="flex flex-col h-full">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-xl ${industry.iconBg} flex items-center justify-center mb-6`}>
                    <Icon className={`h-8 w-8 ${industry.iconColor}`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{industry.name}</h3>

                  {/* Description */}
                  <p className="text-slate-700 mb-6 leading-relaxed flex-grow">{industry.description}</p>

                  {/* Opportunities */}
                  <div className="space-y-3">
                    <div className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Key Opportunities:</div>
                    <div className="flex flex-wrap gap-2">
                      {industry.opportunities.map((opp, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-white/80 text-slate-700 hover:bg-white border border-slate-200">
                          {opp}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="inline-block p-8 bg-gradient-to-r from-[#8B1538] to-[#6B1028] border-0 shadow-xl text-white">
            <p className="text-lg mb-4">
              <span className="font-bold">Telfer Gateway Society has over 30 industry partners</span> across engineering, science, and law sectors.
            </p>
            <p className="text-white/90">
              We're expanding our network every month. Join us to access opportunities you won't find anywhere else.
            </p>
          </Card>
        </div>
      </div>
    </section>
  );
};
